import { format as formatFns, isValid } from "date-fns";

/**
 * Display formatting.
 *
 * Two rules run through this file.
 *
 * 1. Money never becomes a JavaScript number. The API sends decimals as strings
 *    because `Prisma.Decimal` does not fit in a float, and parsing one here to add a
 *    thousands separator would quietly round a lakh-scale invoice. Grouping is done
 *    on the digits themselves.
 * 2. A billing date is a *date*, not an instant. Values that arrive as a plain date
 *    or as UTC midnight are read in UTC, so a vehicle billed on the 1st never shows
 *    as the 31st for someone whose laptop is set to a western timezone.
 */

const EM_DASH = "—";

const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

type DateInput = Date | string | number | null | undefined;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const UTC_MIDNIGHT = /^\d{4}-\d{2}-\d{2}T00:00:00(\.0+)?Z$/;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? date : null;
}

/** True when the value carries no meaningful time-of-day and must be read in UTC. */
function isCalendarDate(value: DateInput): boolean {
  if (value instanceof Date || typeof value === "number") return false;
  if (typeof value !== "string") return false;
  return DATE_ONLY.test(value) || UTC_MIDNIGHT.test(value);
}

/** `14 Mar 2026`. Calendar-only values are read in UTC; `—` when absent or unparseable. */
export function formatDate(
  value: DateInput,
  fallback: string = EM_DASH,
): string {
  const date = toDate(value);
  if (!date) return fallback;
  if (isCalendarDate(value)) {
    return `${String(date.getUTCDate()).padStart(2, "0")} ${MONTHS_SHORT[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
  }
  return formatFns(date, "dd MMM yyyy");
}

/** `14 Mar 2026, 16:42` — always local, because an instant is an instant. */
export function formatDateTime(
  value: DateInput,
  fallback: string = EM_DASH,
): string {
  const date = toDate(value);
  if (!date) return fallback;
  return formatFns(date, "dd MMM yyyy, HH:mm");
}

/** `14 Mar 2026, 16:42:07` for audit trails and sync logs. */
export function formatDateTimeSeconds(
  value: DateInput,
  fallback: string = EM_DASH,
): string {
  const date = toDate(value);
  if (!date) return fallback;
  return formatFns(date, "dd MMM yyyy, HH:mm:ss");
}

/**
 * `July 2026`. Accepts the `YYYY-MM` shape the filter vocabulary uses, and any date.
 * Short form gives `Jul 2026` for chart axes where the long name will not fit.
 */
export function formatMonth(
  value: DateInput,
  options: { short?: boolean } = {},
  fallback: string = EM_DASH,
): string {
  const names = options.short ? MONTHS_SHORT : MONTHS_LONG;

  if (typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) {
    const year = Number(value.slice(0, 4));
    const monthIndex = Number(value.slice(5, 7)) - 1;
    const name = names[monthIndex];
    if (!name || Number.isNaN(year)) return fallback;
    return `${name} ${year}`;
  }

  const date = toDate(value);
  if (!date) return fallback;
  if (isCalendarDate(value)) {
    return `${names[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
  }
  return `${names[date.getMonth()]} ${date.getFullYear()}`;
}

/** `2026-07` — the wire form the `billingMonth` filter expects. */
export function toMonthKey(value: DateInput): string | null {
  const date = toDate(value);
  if (!date) return null;
  const useUtc = isCalendarDate(value);
  const year = useUtc ? date.getUTCFullYear() : date.getFullYear();
  const month = (useUtc ? date.getUTCMonth() : date.getMonth()) + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
}

/** `YYYY-MM-DD` — the wire form the billing-date range filters expect. */
export function toDateKey(value: DateInput): string | null {
  const date = toDate(value);
  if (!date) return null;
  if (isCalendarDate(value)) return date.toISOString().slice(0, 10);
  return formatFns(date, "yyyy-MM-dd");
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

const inGrouping = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 20,
});

/** Indian grouping: 12,34,567. Accepts numbers or numeric strings. */
export function formatNumber(
  value: number | string | null | undefined,
  options: { fractionDigits?: number } = {},
  fallback: string = EM_DASH,
): string {
  if (value === null || value === undefined || value === "") return fallback;

  if (typeof value === "string") {
    // Strings may be arbitrary-precision; group the digits rather than parsing.
    const grouped = groupIndianDecimalString(value);
    if (grouped !== null) return grouped;
    return fallback;
  }

  if (!Number.isFinite(value)) return fallback;
  if (options.fractionDigits !== undefined) {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: options.fractionDigits,
      maximumFractionDigits: options.fractionDigits,
    }).format(value);
  }
  return inGrouping.format(value);
}

/**
 * Groups the integer part of a decimal *string* the Indian way — last three digits,
 * then pairs — without ever converting it to a float. `"1234567.50"` → `"12,34,567.50"`.
 * Returns null when the string is not a plain decimal.
 */
export function groupIndianDecimalString(raw: string): string | null {
  const trimmed = raw.trim();
  const match = /^([+-]?)(\d+)(?:\.(\d*))?$/.exec(trimmed);
  if (!match) return null;

  const sign = match[1] === "-" ? "-" : "";
  const digits = match[2] ?? "";
  const fraction = match[3];

  let grouped: string;
  if (digits.length <= 3) {
    grouped = digits;
  } else {
    const head = digits.slice(0, -3);
    const tail = digits.slice(-3);
    const pairs: string[] = [];
    let rest = head;
    while (rest.length > 2) {
      pairs.unshift(rest.slice(-2));
      rest = rest.slice(0, -2);
    }
    if (rest.length) pairs.unshift(rest);
    grouped = `${pairs.join(",")},${tail}`;
  }

  return fraction !== undefined && fraction.length > 0
    ? `${sign}${grouped}.${fraction}`
    : `${sign}${grouped}`;
}

export interface CurrencyOptions {
  /** Show the ₹ symbol. On by default. */
  symbol?: boolean;
  /** Force two decimals, or drop them entirely for headline figures. */
  decimals?: 0 | 2 | "auto";
  /** Render 1,25,00,000 as "1.25 Cr" for stat cards where the digits do not fit. */
  compact?: boolean;
}

/**
 * Formats the decimal *string* the API sends for money.
 *
 * The value is never passed through `Number` for display: the integer digits are
 * grouped as text and the fraction is truncated or padded as text. The one exception
 * is compact mode, which is an explicit approximation for headline tiles and says so
 * by rounding to two decimal places of a crore or lakh.
 */
export function formatCurrencyINR(
  value: string | number | null | undefined,
  options: CurrencyOptions = {},
  fallback: string = EM_DASH,
): string {
  const { symbol = true, decimals = "auto", compact = false } = options;
  if (value === null || value === undefined || value === "") return fallback;

  const raw =
    typeof value === "number"
      ? Number.isFinite(value)
        ? value.toFixed(2)
        : ""
      : value;
  if (!raw) return fallback;

  const match = /^([+-]?)(\d+)(?:\.(\d*))?$/.exec(raw.trim());
  if (!match) return fallback;

  const sign = match[1] === "-" ? "-" : "";
  const digits = match[2] ?? "0";
  const fractionRaw = match[3] ?? "";
  const prefix = symbol ? "₹" : "";

  if (compact) {
    return `${sign}${prefix}${compactIndian(digits, fractionRaw)}`;
  }

  let fraction: string;
  if (decimals === 0) {
    fraction = "";
  } else if (decimals === 2) {
    fraction = `.${(fractionRaw + "00").slice(0, 2)}`;
  } else {
    // "auto": keep paise only when there are any.
    const trimmed = fractionRaw.replace(/0+$/, "");
    fraction = trimmed ? `.${(fractionRaw + "00").slice(0, 2)}` : "";
  }

  const grouped = groupIndianDecimalString(digits) ?? digits;
  return `${sign}${prefix}${grouped}${fraction}`;
}

/**
 * Crore / lakh / thousand shorthand for headline tiles. Works on the digit string so
 * a fourteen-digit invoice total is still scaled correctly, and states the unit rather
 * than relying on the reader to count commas.
 */
function compactIndian(digits: string, fraction: string): string {
  const length = digits.length;
  const scale = (unitDigits: number, unit: string): string => {
    const wholeLength = length - unitDigits;
    const whole = digits.slice(0, wholeLength) || "0";
    const rest = (digits.slice(wholeLength) + "00")
      .slice(0, 2)
      .replace(/0+$/, "");
    const groupedWhole = groupIndianDecimalString(whole) ?? whole;
    return rest ? `${groupedWhole}.${rest} ${unit}` : `${groupedWhole} ${unit}`;
  };

  if (length > 7) return scale(7, "Cr");
  if (length > 5) return scale(5, "L");
  if (length > 3) return scale(3, "K");
  const trimmed = fraction.replace(/0+$/, "");
  return trimmed ? `${digits}.${trimmed.slice(0, 2)}` : digits;
}

/** `1 day`, `42 days`, `—`. The unit is always shown; a bare number is not an age. */
export function formatDays(
  days: number | null | undefined,
  fallback: string = EM_DASH,
): string {
  if (days === null || days === undefined || !Number.isFinite(days))
    return fallback;
  const whole = Math.max(0, Math.round(days));
  return `${formatNumber(whole)} ${pluralise(whole, "day")}`;
}

/** The noun for a count. `pluralise(1, 'vehicle')` → `vehicle`; `pluralise(3, 'vehicle')` → `vehicles`. */
export function pluralise(
  count: number,
  singular: string,
  plural?: string,
): string {
  if (Math.abs(count) === 1) return singular;
  return plural ?? `${singular}s`;
}

/** `1 vehicle` / `12 vehicles` — count and noun together, grouped Indian-style. */
export function countOf(
  count: number | null | undefined,
  singular: string,
  plural?: string,
): string {
  const n = count ?? 0;
  return `${formatNumber(n)} ${pluralise(n, singular, plural)}`;
}

/** `12.4%`, from a ratio or an already-scaled percentage. */
export function formatPercent(
  value: number | null | undefined,
  options: { fractionDigits?: number; fromRatio?: boolean } = {},
  fallback: string = EM_DASH,
): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return fallback;
  const scaled = options.fromRatio ? value * 100 : value;
  const digits = options.fractionDigits ?? (Math.abs(scaled) >= 10 ? 0 : 1);
  return `${scaled.toFixed(digits)}%`;
}

/** `+12` / `−3` / `0` — a signed delta, using a real minus sign. */
export function formatDelta(
  value: number | null | undefined,
  fallback: string = EM_DASH,
): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return fallback;
  if (value === 0) return "0";
  return value > 0
    ? `+${formatNumber(value)}`
    : `−${formatNumber(Math.abs(value))}`;
}

/** `2 hours ago`, `just now` — for sync timestamps, where recency is the point. */
export function formatRelative(
  value: DateInput,
  fallback: string = EM_DASH,
): string {
  const date = toDate(value);
  if (!date) return fallback;
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  const future = seconds < 0;
  const abs = Math.abs(seconds);

  const say = (n: number, unit: string) =>
    future ? `in ${n} ${pluralise(n, unit)}` : `${n} ${pluralise(n, unit)} ago`;

  if (abs < 45) return future ? "shortly" : "just now";
  if (abs < 3600) return say(Math.round(abs / 60), "minute");
  if (abs < 86_400) return say(Math.round(abs / 3600), "hour");
  if (abs < 2_592_000) return say(Math.round(abs / 86_400), "day");
  return formatDate(date);
}

/** Sentence-case a raw enum or sheet value: `SUB_DEALER` → `Sub dealer`. */
export function humanise(
  value: string | null | undefined,
  fallback: string = EM_DASH,
): string {
  if (!value) return fallback;
  // camelCase splits into words too: "paymentModeRaw" read as "Paymentmoderaw"
  // in the audit history until 4 Sep 2026.
  const words = value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  if (!words) return fallback;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Sheet strings arrive shouting — party names, buyer names and remarks are typed
 * in caps on the source sheet, and verbatim they out-shout everything the product
 * says for itself. Anything that is entirely upper case is re-set in title case
 * for display; a mixed-case string passes through untouched, because someone
 * already chose that casing on purpose. Display only — never feed the result
 * back into a filter, a search or a payload, where the raw string is the key.
 */
export function displayName(
  value: string | null | undefined,
  fallback: string = EM_DASH,
): string {
  if (!value) return fallback;
  const collapsed = value.trim().replace(/\s+/g, " ");
  if (!collapsed) return fallback;
  if (collapsed !== collapsed.toUpperCase()) return collapsed;
  return collapsed
    .toLowerCase()
    .replace(
      /(^|[\s\-–/(])(\p{L})/gu,
      (match, lead: string, letter: string) => lead + letter.toUpperCase(),
    );
}

export { EM_DASH };
